/**
 * `useStoreResolvedParticipants` — upgrades plain-email participants into
 * entity-bearing participants via the CRM object store.
 *
 * Problem this solves: every section that renders a list of email-addressed
 * participants (email-thread participants, meeting attendees, etc.) shows
 * those participants as chips. When the email already corresponds to a
 * resolved CRM entity (`ahoy_contact` / `ahoy_user`) the chip should display
 * that entity (name, avatar, slideout-on-click, hover popover with the right
 * CTAs). When it doesn't, the chip falls back to a plain-email pill that
 * opens the Create Contact modal.
 *
 * The naive approach — relying only on the parent entity's associations —
 * misses contacts that exist in the org but aren't directly associated with
 * the parent (e.g. an attendee on a meeting that predates the association,
 * or a participant on an email thread whose contact was created from a
 * different thread).
 *
 * This hook fires `ensureEmailResolution(email, orgId)` for each plain-email
 * participant on mount (idempotent / deduped), subscribes to the store's
 * global snapshot, and re-renders when the store's email index populates.
 * Plain-email participants whose store lookup yields an entity get their
 * `object` field filled in.
 *
 * The store IS the source of truth for email→entity resolution; this hook is
 * just a thin orchestrator around it. See `usePersonByEmail` for the per-
 * hook flavor and `crmObjectStore.emailIndex` for the underlying index.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import type { Participant } from '@/lib/crm/components/ParticipantPillList';
import { isPersonType } from '@/lib/crm/core/entityTypes';
import {
  ensureEmailResolution,
  setActiveOrgIdForEmailResolution,
} from '@/lib/crm/hooks/usePersonByEmail';
import {
  getEmailIndexSnapshot,
  getSnapshot as getStoreSnapshot,
  resolveEntityId,
  subscribe as subscribeToStore,
} from '@/lib/crm/store/crmObjectStore';
import type { CrmObject } from '@/types/crm';

interface UseStoreResolvedParticipantsArgs {
  /**
   * The unresolved-but-ideally-resolvable participants. Each entry should
   * have an `email` and may or may not already have an `object`. The hook
   * only attempts resolution for entries without an `object`.
   */
  participants: Participant[];
  /** Org ID for the email→entity API call. Must be positive to trigger lookups. */
  orgId: number;
}

/**
 * Returns a new `participants` array where every plain-email entry has its
 * `object` populated when the email resolves through the store's email
 * index. Other entries pass through unchanged. The return is referentially
 * stable across re-renders when no resolution changes.
 */
export function useStoreResolvedParticipants({
  participants,
  orgId,
}: UseStoreResolvedParticipantsArgs): Participant[] {
  // List of emails that need resolution (no resolved object yet, has email).
  const plainEmailList = useMemo(
    () =>
      participants
        .filter(p => !p.object && p.email)
        .map(p => p.email as string),
    [participants]
  );

  // Keep the active-org guard in sync. `_searchAndPopulate` drops late
  // responses whose `orgId` doesn't match `activeOrgIdForEmailResolution`,
  // so without this update a participants list mounted for a new org
  // before any `usePersonByEmail` hook has fired would see ALL its
  // lookups silently dropped because the module-level guard still held
  // the previous org's id.
  useEffect(() => {
    if (orgId && orgId > 0) {
      setActiveOrgIdForEmailResolution(orgId);
    }
  }, [orgId]);

  // Fire-and-forget search-and-seed for each plain email. ensureEmailResolution
  // dedupes concurrent calls per (orgId, email) via the in-flight map and
  // returns immediately when the index already holds a definitive answer for
  // the address. We deliberately do NOT pre-check the index snapshot here: a
  // positive mapping is only definitive when it points at a person entity, and
  // that judgement lives in `ensureEmailResolution` — a stale mapping to a
  // non-person entity must still get its one-shot recovery search so a real
  // contact / user in the backend is not hidden by teardown cache state.
  useEffect(() => {
    if (!orgId || orgId <= 0) {
      return;
    }
    for (const email of plainEmailList) {
      void ensureEmailResolution(email, orgId);
    }
  }, [plainEmailList, orgId]);

  // Subscribe to the store's global snapshot. The snapshot reference (the
  // `objects` Map) changes on every `commitObjectMutation`, which is exactly
  // when we want to re-evaluate email→entity resolution. A single global
  // subscription is cheap and avoids per-email subscription churn.
  const subscribeStore = useCallback(
    (cb: () => void) => subscribeToStore(cb),
    []
  );
  const storeSnapshot = useSyncExternalStore(subscribeStore, getStoreSnapshot);

  const resolvedPlainEmails = useMemo(() => {
    const map = new Map<string, CrmObject>();
    for (const email of plainEmailList) {
      const indexed = getEmailIndexSnapshot(email);
      if (typeof indexed !== 'string') {
        continue;
      }
      // Resolve through the alias map so pre-merge ids surface the canonical
      // entry (e.g. when a contact record was merged into another, its
      // pre-merge id lives in the alias map pointing to the canonical id).
      const canonicalId = resolveEntityId(indexed);
      const stored = storeSnapshot.get(canonicalId);
      // Only upgrade to a resolved entity badge when the store entry is a
      // person type (contact / user). A non-person entity that still carries
      // this email in the store's email index — stale cache state left behind
      // by a torn-down entity type — must NOT resolve here; leaving the
      // participant plain preserves the plain-badge → Create Contact fallback
      // for such stale records, and the recovery search fired above re-points
      // the index when a real contact / user exists.
      if (stored?.data && isPersonType(stored.data.entity_type_name)) {
        map.set(email, stored.data);
      }
    }
    return map;
  }, [plainEmailList, storeSnapshot]);

  // Upgrade plain-email participants whose store lookup now yields an entity.
  // The chip flips from "raw email" to a full contact / user badge —
  // display name + avatar + click opens the entity's slideout instead of
  // the Create Contact modal.
  return useMemo<Participant[]>(() => {
    if (resolvedPlainEmails.size === 0) {
      return participants;
    }
    return participants.map(p => {
      if (p.object || !p.email) {
        return p;
      }
      const resolvedObj = resolvedPlainEmails.get(p.email);
      if (!resolvedObj) {
        return p;
      }
      return { ...p, object: resolvedObj };
    });
  }, [participants, resolvedPlainEmails]);
}
